import React, { useState, useCallback, useRef } from "react"
import {
  Button,
  Progress,
} from 'reactstrap'
import classNames from 'classnames'
import Dropzone from 'react-dropzone'

import api from '../../../api'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import axios, { Canceler } from "axios"

const scaleBetween = (unscaledNum: number, minAllowed: number, maxAllowed: number, min: number, max: number) => {
  return (maxAllowed - minAllowed) * (unscaledNum - min) / (max - min) + minAllowed
}

interface UploaderProps {
  reloadJobs: () => void
}

interface UploaderState {
  started: boolean
  inProgress: boolean
  progressPercent: number

  file?: File
  fileName?: string
  fileKey?: string
}

type LanguageCode = "en-US" | "ja-JP"

const INITIAL_STATE: UploaderState = {
  started: false,
  inProgress: false,
  progressPercent: 1,
}

const Uploader: React.FC<UploaderProps> = ({ reloadJobs }) => {
  const [ canceler, setCanceler ] = useState<Canceler | null>(null)
  const [ state, setState ] = useState<UploaderState>(INITIAL_STATE)
  const selectLanguageRef = useRef<HTMLSelectElement>(null)

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      setState({
        inProgress: true,
        started: true,
        progressPercent: 5,
        file
      })

      try {
        const resp = await api.uploads.presign()
        const { fileName, key } = await api.uploadToS3(
          resp.data,
          file,
          (percentCompleted) => {
            setState(ps => ({
              ...ps,
              progressPercent: scaleBetween(percentCompleted, 10, 100, 0, 100),
            }))
          },
          (canceler) => {
            setCanceler(() => canceler)
          }
        )

        const jobData = {
          job_name: fileName,
          file_key: key,
          language_code: selectLanguageRef.current?.value,
        }
        const createJobResp = await api.jobs.create(jobData)

        if (createJobResp.data.error === false) {
          setState(INITIAL_STATE)
          reloadJobs()
        } else {
          alert('Upload failed, please try again.')
          setState(INITIAL_STATE)
        }
      } catch (e) {
        if (axios.isCancel(e)) {
          setState(INITIAL_STATE)
        } else {
          throw e
        }
      }
    },
    [ reloadJobs, setState, setCanceler, selectLanguageRef ]
  )

  const cancel = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      if (canceler) {
        canceler('Upload canceled by user.')
        setCanceler(null)
      }
    },
    [ canceler ]
  )

  const dropzoneWrapperClasses = classNames(
    "mb-2",
    {"d-none": state.started}
  )

  const newJobWrapperClasses = classNames(
    "card",
    "bg-light",
    "text-dark",
    "shadow-sm",
    // {"d-none": !state.started}
  )

  const newJobProgressClasses = classNames(
    "mb-2",
    {"d-none": !state.inProgress}
  )

  return (
    <div className={newJobWrapperClasses}>
      <div className="p-4 card-body">
        <div className="mb-2">
          <label htmlFor="language-code">
            Source audio language
          </label>
          <select
            className="custom-select"
            name="language-code"
            id="language-code"
            ref={selectLanguageRef}
            disabled={state.started}
          >
            <option value="en-US">English (United States)</option>
            <option value="ja-JP">Japanese (日本語)</option>
          </select>
        </div>

        <div className={dropzoneWrapperClasses}>
          <Dropzone multiple={false} onDrop={handleDrop}>
            {({getRootProps, getInputProps, isDragActive}) => {
              return (
                <div
                  {...getRootProps()}
                  className={classNames('p-4', 'text-center', 'card-body', 'job-uploader-dz', 'dropzone', {'dropzone--isActive': isDragActive})}
                >
                  <input {...getInputProps()} />
                  {
                    isDragActive ?
                      <span><FontAwesomeIcon icon='cloud-upload-alt' /> Drop here...</span> :
                      <span><FontAwesomeIcon icon='cloud-upload-alt' /> Drop a video or audio file to transcribe here, or click to select a file.</span>
                  }
                </div>
              )
            }}
          </Dropzone>
        </div>

        <Progress animated value={state.progressPercent} className={newJobProgressClasses} />

        <Button
          color="secondary"
          type="button"
          disabled={!canceler}
          onClick={cancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}

export default Uploader
